import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { organizationService } from '../../services/organizationService';
import { AIAssistantMessage, AIAssistantResponse } from '../../types/organizations';
import AIAssistantModal from './AIAssistantModal';

interface AIAssistantPanelProps {
  organizationId: string;
  organizationName: string | null;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
  onClose: () => void;
  isVisible?: boolean;
}

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  organizationId,
  organizationName,
  accessToken,
  setUserAndToken,
  onClose,
  isVisible = true
}) => {
  const [messages, setMessages] = useState<AIAssistantMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<AIAssistantMessage | null>(null);
  const [showModal, setShowModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: AIAssistantMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const { job_id } = await organizationService.answerCCARQuestion(
        organizationId,
        { question: inputValue.trim() },
        accessToken,
        setUserAndToken
      );

      // Save job tracking and start polling
      setJobId(job_id);
      setJobStatus('queued');
      setIsLoading(true);
      localStorage.setItem(`ai_assistant_job_${organizationId}`, job_id);
      localStorage.setItem(`ai_assistant_status_${organizationId}`, 'queued');
      localStorage.setItem(`ai_assistant_question_${organizationId}`, inputValue.trim());
      pollJobStatus(job_id, inputValue.trim());
    } catch (error) {
      const errorMessage: AIAssistantMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };
  const pollJobStatus = async (jobIdToPoll: string, askedQuestion?: string) => {
    try {
      const result = await organizationService.getJobStatus(jobIdToPoll, accessToken, setUserAndToken);
      setJobStatus(result.status);
      localStorage.setItem(`ai_assistant_status_${organizationId}`, result.status);

      if (result.status === 'finished') {
        const data = result.result || {};
        const payload = data.result || data; // worker returns {status, message, result}
        const final = payload?.answer ? payload : payload?.data || payload;
        const assistantMessage: AIAssistantMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: final?.answer || 'No answer produced.',
          timestamp: new Date(),
          metadata: {
            question: askedQuestion || localStorage.getItem(`ai_assistant_question_${organizationId}`) || '',
            reasoning: final?.reasoning,
            data_sources: final?.data_sources,
            confidence: final?.confidence
          }
        };
        setMessages(prev => [...prev, assistantMessage]);
        // cleanup
        setTimeout(() => {
          setJobId(null);
          setJobStatus(null);
          setIsLoading(false);
          localStorage.removeItem(`ai_assistant_job_${organizationId}`);
          localStorage.removeItem(`ai_assistant_status_${organizationId}`);
          localStorage.removeItem(`ai_assistant_question_${organizationId}`);
        }, 3000);
      } else if (result.status === 'failed') {
        const errorMessage: AIAssistantMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: `Sorry, the AI assistant job failed: ${result.error || 'Unknown error'}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        setJobId(null);
        setJobStatus(null);
        setIsLoading(false);
        localStorage.removeItem(`ai_assistant_job_${organizationId}`);
        localStorage.removeItem(`ai_assistant_status_${organizationId}`);
        localStorage.removeItem(`ai_assistant_question_${organizationId}`);
      } else {
        setTimeout(() => pollJobStatus(jobIdToPoll, askedQuestion), 3000);
      }
    } catch (err) {
      // Stop polling on error
      setJobId(null);
      setJobStatus(null);
      setIsLoading(false);
      localStorage.removeItem(`ai_assistant_job_${organizationId}`);
      localStorage.removeItem(`ai_assistant_status_${organizationId}`);
      localStorage.removeItem(`ai_assistant_question_${organizationId}`);
    }
  };

  // Resume polling on mount or when org changes
  useEffect(() => {
    const existingJobId = localStorage.getItem(`ai_assistant_job_${organizationId}`);
    const existingStatus = localStorage.getItem(`ai_assistant_status_${organizationId}`);
    if (existingJobId && existingStatus && !['finished', 'failed'].includes(existingStatus)) {
      setJobId(existingJobId);
      setJobStatus(existingStatus);
      setIsLoading(true);
      pollJobStatus(existingJobId);
    }
  }, [organizationId]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInfoClick = (message: AIAssistantMessage) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Render markdown with proper line breaks (no extra spacing)
  const renderMessage = (content: string) => {
    // First, unescape common escape sequences from the JSON string
    // Handle \n, \$, \-, etc. before HTML escaping
    let text = content
      .replace(/\\n/g, '\n')  // Convert \n to actual newlines
      .replace(/\\\$/g, '$')  // Remove unnecessary escaping of $
      .replace(/\\-/g, '-')   // Remove unnecessary escaping of -
      .replace(/\\\*/g, '*')  // Remove unnecessary escaping of *
      .replace(/\\:/g, ':');  // Remove unnecessary escaping of :
    
    // HTML-escape the content to protect special characters like <, >, &
    const escapeHtml = (str: string) => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };
    
    let html = escapeHtml(text);
    
    // Convert newlines to <br /> tags (after escaping)
    html = html.replace(/\n/g, '<br />');
    
    // Apply markdown conversions on escaped text
    // Bold: *text* (primary), also support legacy **text** and __text__
    html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<strong>$1</strong>');
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Italic: _text_ only (single underscores)
    html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');
    
    // Sanitize HTML to prevent XSS attacks
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['strong', 'em', 'br', 'b', 'i'],
      ALLOWED_ATTR: []
    });
    
    return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  return (
    <>
      {/* Panel */}
      <div className={`fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/ARiA-Logo-PrimaryLightBlue-Transparent-BG.png" 
                alt="ARiA Logo" 
                className="w-8 h-8"
              />
              <h2 className="text-lg font-semibold text-gray-900">ARiA Panel</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {organizationName ? (
            <div className="px-4 pb-3">
              <div className="text-sm text-blue-700 bg-blue-100 rounded-lg px-3 py-2">
                <span className="font-medium">Talking about</span> {organizationName}
              </div>
            </div>
          ) : (
            <div className="px-4 pb-3">
              <div className="text-sm text-gray-600 bg-gray-100 rounded-lg px-3 py-2">
                <span className="font-medium">General AI Assistant</span> - Navigate to an organization to get specific insights
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 mt-8">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="text-sm">
                {organizationName 
                  ? `Ask me anything about credit control and accounts receivable for ${organizationName}.`
                  : "Ask me anything about credit control and accounts receivable. Navigate to an organization for specific insights."
                }
              </p>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="text-sm">
                  {renderMessage(message.content)}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs ${message.type === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {formatTimestamp(message.timestamp)}
                  </span>
                  {message.type === 'assistant' && message.metadata && (
                    <button
                      onClick={() => handleInfoClick(message)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Info</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {(isLoading || (jobId && (jobStatus === 'queued' || jobStatus === 'started'))) && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about credit control, accounts receivable, customer analysis..."
              className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!Boolean(inputValue.trim()) || Boolean(isLoading) || Boolean(jobId && (jobStatus === 'queued' || jobStatus === 'started'))}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedMessage && (
        <AIAssistantModal
          message={selectedMessage}
          onClose={() => {
            setShowModal(false);
            setSelectedMessage(null);
          }}
        />
      )}
    </>
  );
};

export default AIAssistantPanel;
